import React, { ReactElement } from "react";
import { DetailsList, DetailsListLayoutMode, IColumn, IDetailsRowProps, mergeStyleSets, useTheme } from "@fluentui/react";
import { SelectionMode } from "@fluentui/utilities";
import { IDaneValidationTestType, DNSSEC_AND_DANE_TEST, DNS_BASELINE_TEST } from "../../constants/RcaDaneValidationTestTypes";
import { IRcaDaneValidationDaneResult } from "../../models/IRcaDaneValidationDaneResult";
import { IRcaDaneValidationDnsSecResult } from "../../models/IRcaDaneValidationDnsSecResult";
import {
  DaneValidationResultTableRowDaneIcon,
  DaneValidationResultTableRowDnsIcon,
  DaneValidationResultTableRowDnsSecIcon,
  DaneValidationResultTableRowTlsaIcon,
} from "./DaneValidationResultTableRowIcons";

/**
 * The table row items structure
 */
export interface IDaneValidationTableItem {
  /**
   * Record type field
   */
  RecordType: string;
  /**
   * Record Value or Address field
   */
  Address: string;
  /**
   * DNSSEC field
   */
  DnsSec: IRcaDaneValidationDnsSecResult | string;
  /**
   * Tlsa value
   */
  Tlsa: string | boolean | null;
  /**
   * Dane result
   */
  Dane: string | IRcaDaneValidationDaneResult;
  /**
   * Record details \ error message
   */
  Details: string | ReactElement;
}

const classNames = mergeStyleSets({
  wrapper: {
    paddingBottom: 16,
    marginTop: -8,
  },
});

/**
 * @param testType Dane Validation test type
 * @returns result table columns
 */
const getResultTableColumns = (testType: IDaneValidationTestType): IColumn[] => {
  const COLUMNS: IColumn[] = [
    {
      key: "recordType",
      name: WebsiteStrings.DaneRecordType,
      fieldName: "RecordType",
      minWidth: 130,
      maxWidth: 180,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "address",
      name: WebsiteStrings.DaneRecordValueAddress,
      fieldName: "Address",
      minWidth: 130,
      maxWidth: 180,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "dnssec",
      name: WebsiteStrings.DaneDnsSec,
      fieldName: "DnsSec",
      minWidth: 50,
      maxWidth: 50,
      isResizable: true,
      isPadded: true,
      onRender: (item: IDaneValidationTableItem) => {
        return <DaneValidationResultTableRowDnsSecIcon dnsSecStatus={item.DnsSec} />;
      },
    },
    {
      key: "tlsa",
      name: WebsiteStrings.DaneTlsa,
      fieldName: "Tlsa",
      minWidth: 50,
      maxWidth: 50,
      isResizable: true,
      isPadded: true,
      onRender: (item: IDaneValidationTableItem) => {
        return <DaneValidationResultTableRowTlsaIcon isUsable={item.Tlsa} />;
      },
    },
    {
      key: "dane",
      name: WebsiteStrings.DaneDane,
      fieldName: "Dane",
      minWidth: 50,
      maxWidth: 50,
      isResizable: true,
      isPadded: true,
      onRender: (item: IDaneValidationTableItem) => {
        return <DaneValidationResultTableRowDaneIcon dane={item.Dane} />;
      },
    },
    {
      key: "details",
      name: WebsiteStrings.DaneDetails,
      fieldName: "Details",
      minWidth: 100,
      maxWidth: 300,
      isResizable: true,
      isPadded: true,
      isMultiline: true,
    },
  ];

  const COLUMNS_DNSSEC_ONLY: IColumn[] = [
    {
      key: "recordType",
      name: WebsiteStrings.DaneRecordType,
      fieldName: "RecordType",
      minWidth: 130,
      maxWidth: 180,
      isResizable: false,
      isPadded: true,
    },
    {
      key: "address",
      name: WebsiteStrings.DaneRecordValueAddress,
      fieldName: "Address",
      minWidth: 130,
      maxWidth: 180,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "dnssec",
      name: WebsiteStrings.DaneDnsSec,
      fieldName: "DnsSec",
      minWidth: 50,
      maxWidth: 50,
      isResizable: true,
      isPadded: true,
      onRender: (item: IDaneValidationTableItem) => {
        return <DaneValidationResultTableRowDnsSecIcon dnsSecStatus={item.DnsSec} />;
      },
    },
    {
      key: "details",
      name: WebsiteStrings.DaneDetails,
      fieldName: "Details",
      minWidth: 100,
      maxWidth: 300,
      isResizable: true,
      isPadded: true,
      isMultiline: true,
    },
  ];

  const COLUMNS_BASELINE_DNS: IColumn[] = [
    {
      key: "recordType",
      name: WebsiteStrings.DaneRecordType,
      fieldName: "RecordType",
      minWidth: 130,
      maxWidth: 180,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "address",
      name: WebsiteStrings.DaneRecordValueAddress,
      fieldName: "Address",
      minWidth: 130,
      maxWidth: 180,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "dns",
      name: WebsiteStrings.DaneDns,
      fieldName: "DnsSec",
      minWidth: 50,
      maxWidth: 50,
      isResizable: true,
      isPadded: true,
      onRender: (item: IDaneValidationTableItem) => {
        return <DaneValidationResultTableRowDnsIcon dnsSecStatus={item.DnsSec} />;
      },
    },
    {
      key: "details",
      name: WebsiteStrings.DaneDetails,
      fieldName: "Details",
      minWidth: 100,
      maxWidth: 300,
      isResizable: true,
      isPadded: true,
      isMultiline: true,
    },
  ];

  return testType === DNSSEC_AND_DANE_TEST ? COLUMNS : testType === DNS_BASELINE_TEST ? COLUMNS_BASELINE_DNS : COLUMNS_DNSSEC_ONLY;
};

/**
 * @param props The component properties
 * @returns The markup
 */
const DaneValidationResultTable: React.FC<{
  /**
   * List of items to render in the table
   */
  items: IDaneValidationTableItem[];
  /**
   * Test Type of the result
   */
  testType: IDaneValidationTestType;
}> = ({ items, testType }) => {
  const columns = getResultTableColumns(testType);
  const theme = useTheme();

  return (
    <DetailsList
      className={classNames.wrapper}
      styles={{
        root: {
          backgroundColor: `var(--rca-control-background, ${theme.semanticColors.bodyBackground})`,
          color: theme.semanticColors.bodyText,
        },
        headerWrapper: {
          backgroundColor: `var(--rca-control-background, ${theme.semanticColors.bodyBackground})`,
        },
        contentWrapper: {
          backgroundColor: `var(--rca-control-background, ${theme.semanticColors.bodyBackground})`,
        },
      }}
      items={items}
      compact={false}
      columns={columns}
      selectionMode={SelectionMode.none}
      getKey={(_, i: number) => i.toString()}
      setKey="none"
      layoutMode={DetailsListLayoutMode.justified}
      isHeaderVisible={true}
      onRenderRow={(props: IDetailsRowProps | undefined, defaultRender) => {
        if (!props || !defaultRender) {
          return null;
        }

        return defaultRender({
          ...props,
          styles: {
            root: {
              backgroundColor: `var(--rca-control-background, ${theme.semanticColors.bodyBackground})`,
              color: theme.semanticColors.bodyText,
            },
            rootHovered: {
              backgroundColor: theme.semanticColors.bodyBackgroundHovered,
              color: theme.semanticColors.bodyText,
            },
            cell: {
              color: theme.semanticColors.bodyText,
            },
          },
        });
      }}
    />
  );
};

export default DaneValidationResultTable;
